// Mail Portal Types & Constants

export interface Email {
  id: string;
  from: string;
  to: string;
  subject: string;
  date: string;
  is_read: boolean;
  has_attachments: boolean;
  message_id: string;
}

export interface EmailDetail {
  id: string;
  from: string;
  to: string;
  cc: string;
  subject: string;
  date: string;
  body: {
    plain: string;
    html: string;
  };
  attachments: Array<{
    filename: string;
    content_type: string;
    size: number;
    data: string;
  }>;
  message_id: string;
  in_reply_to: string;
  references: string;
}

export interface Member {
  id: string;
  email: string;
  name: string;
  role: string;
  apartment_id: string;
}

export interface Template {
  id: string;
  name: string;
  subject: string;
  body_html: string;
  body_plain?: string;
  icon_id?: string;
  header_color?: string;
  sort_order?: number;
}

export type View = 'inbox' | 'compose' | 'read' | 'templates' | 'sent' | 'drafts';

// Available icons for templates
export const TEMPLATE_ICONS = [
  { id: 'info', name: 'Information', icon: 'ℹ️', svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>' },
  { id: 'alert', name: 'Varning', icon: '⚠️', svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>' },
  { id: 'calendar', name: 'Kalender', icon: '📅', svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>' },
  { id: 'meeting', name: 'Möte', icon: '👥', svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>' },
  { id: 'maintenance', name: 'Underhåll', icon: '🔧', svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>' },
  { id: 'money', name: 'Ekonomi', icon: '💰', svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>' },
  { id: 'document', name: 'Dokument', icon: '📄', svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>' },
  { id: 'home', name: 'Fastighet', icon: '🏠', svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>' },
  { id: 'bell', name: 'Påminnelse', icon: '🔔', svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>' },
  { id: 'check', name: 'Bekräftelse', icon: '✅', svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>' },
  { id: 'star', name: 'Viktigt', icon: '⭐', svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>' },
  { id: 'none', name: 'Ingen ikon', icon: '—', svg: '' },
];

// Available header colors for templates  
export const TEMPLATE_COLORS = [
  { id: 'blue', name: 'Blå', color: '#2563eb', light: '#dbeafe' },
  { id: 'green', name: 'Grön', color: '#16a34a', light: '#dcfce7' },
  { id: 'red', name: 'Röd', color: '#dc2626', light: '#fee2e2' },
  { id: 'orange', name: 'Orange', color: '#ea580c', light: '#ffedd5' },
  { id: 'purple', name: 'Lila', color: '#9333ea', light: '#f3e8ff' },
  { id: 'teal', name: 'Turkos', color: '#0d9488', light: '#ccfbf1' },
  { id: 'gray', name: 'Grå', color: '#4b5563', light: '#f3f4f6' },
  { id: 'pink', name: 'Rosa', color: '#db2777', light: '#fce7f3' },
];

// Board member roles that have access
export const BOARD_ROLES = ['board', 'board_member', 'admin', 'superadmin', 'styrelsemedlem', 'styrelse'];

// API Base URL
export const API_BASE = process.env.REACT_APP_MAIL_API_URL || process.env.REACT_APP_BACKEND_URL || '';

// Helper function to sort members by apartment number
export const sortMembersByApartment = (members: Member[]): Member[] => {
  return [...members].sort((a, b) => {
    const aptA = a.apartment_id || '';
    const aptB = b.apartment_id || '';
    
    const parseApartment = (apt: string) => {
      const match = apt.match(/^(\d+)([A-Za-z]*)$/);
      if (match) {
        return { num: parseInt(match[1], 10), letter: match[2].toUpperCase() };
      }
      const numMatch = apt.match(/(\d+)/);
      return { num: numMatch ? parseInt(numMatch[1], 10) : 9999, letter: apt };
    };
    
    const parsedA = parseApartment(aptA);
    const parsedB = parseApartment(aptB);
    
    if (parsedA.num !== parsedB.num) {
      return parsedA.num - parsedB.num;
    }
    return parsedA.letter.localeCompare(parsedB.letter);
  });
};
